import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ShieldCheck, Plus, Check } from "lucide-react";
import { toast } from "sonner";

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [editing, setEditing] = useState(null); // role or {new:true}
  const [name, setName] = useState("");
  const [perms, setPerms] = useState([]);
  const [scope, setScope] = useState("all");
  const SCOPES = [["all", "Everything", "Sees every client, board and item in the company"], ["team", "Their team", "Sees clients owned by their team, boards they're on, and items they're involved in"], ["own", "Only their own", "Sees only boards they're a member of and items assigned to them"]];

  const load = () => api.get("/admin/roles").then((r) => setRoles(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/admin/permissions").then((r) => setCatalog(r.data)).catch(() => {}); }, []);

  const groups = catalog.reduce((acc, p) => { (acc[p.group] = acc[p.group] || []).push(p); return acc; }, {});

  const openEdit = (role) => {
    setEditing(role || { new: true });
    setName(role?.name || "");
    setPerms(role?.permissions?.includes("*") ? catalog.map((c) => c.key) : role?.permissions || []);
    setScope(role?.data_scope || "all");
  };
  const toggle = (k) => setPerms((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const save = async () => {
    try {
      const body = { name, permissions: perms, data_scope: scope };
      if (editing.new) await api.post("/admin/roles", body);
      else await api.put(`/admin/roles/${editing.id}`, body);
      toast.success("Role saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Roles & Permissions" subtitle="Three axes: what a role can do (capabilities), which records it sees (data scope), and sensitive fields (e.g. deal amounts need 'See deal amounts'). Backend-enforced; the UI only reflects it.">
        <Button onClick={() => openEdit(null)} data-testid="role-create-button"><Plus className="w-4 h-4 mr-1" /> New role</Button>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-roles-grid">
        {roles.map((r) => (
          <button key={r.id} onClick={() => openEdit(r)} data-testid={`role-card-${r.id}`} className="text-left bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md transition-all">
            <div className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-blue-600" /><span className="font-heading font-semibold text-slate-900">{r.name}</span></div>
            <p className="text-sm text-slate-500 mt-2">{r.permissions?.includes("*") ? "All permissions" : `${r.permissions?.length || 0} permissions`} · <span className="capitalize" data-testid={`role-scope-${r.id}`}>{r.data_scope === "team" ? "team scope" : r.data_scope === "own" ? "own scope" : "sees everything"}</span></p>
            {r.is_system && <span className="text-[10px] uppercase tracking-wide text-slate-400 mt-2 inline-block">System role</span>}
          </button>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl" data-testid="role-editor-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing?.new ? "New role" : `Edit · ${editing?.name}`}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div className="space-y-1.5"><Label>Role name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="role-name-input" /></div>
            <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Data scope — which records this role can see</p>
              <div className="grid sm:grid-cols-3 gap-2">{SCOPES.map(([k, l, d]) => <button key={k} type="button" onClick={() => setScope(k)} data-testid={`scope-${k}`} className={`text-left px-3 py-2 rounded-lg border text-sm transition-colors ${scope === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}><p className="font-medium">{l}</p><p className={`text-[11px] mt-0.5 ${scope === k ? "text-slate-300" : "text-slate-400"}`}>{d}</p></button>)}</div></div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Capabilities — what this role can do</p>
            {Object.entries(groups).map(([g, items]) => (
              <div key={g}>
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{g}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {items.map((p) => (
                    <button key={p.key} type="button" onClick={() => toggle(p.key)} data-testid={`perm-${p.key}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition-colors ${perms.includes(p.key) ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                      <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 ${perms.includes(p.key) ? "bg-blue-600" : "border border-slate-300"}`}>{perms.includes(p.key) && <Check className="w-3 h-3 text-white" />}</span>
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={!name} data-testid="role-save-button">Save role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
